import React, { useState } from "react";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ClientType } from "../../types";
import { CSVExporter } from "../../lib/csvExport";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import {
  FormControl,
  FormMessage,
} from "../ui/form";
import { useToast } from "../ui/use-toast";
import { Download, FileSpreadsheet, User, Info } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Separator } from "../ui/separator";

interface ExportClientModalProps {
  client?: ClientType;
  clients?: ClientType[];
  trigger?: React.ReactNode;
  onSuccess?: () => void;
}

// Form validation schema
const exportClientSchema = z.object({
  format: z.literal('csv'),
  includePersonalInfo: z.boolean(),
  includeFinancialInfo: z.boolean(),
  includeCrmData: z.boolean(),
  includePolicyInfo: z.boolean(),
  includeComparisons: z.boolean(),
  includeFederalTSP: z.boolean(),
  includeFegli: z.boolean(),
  jsonHandling: z.enum(['stringify', 'flatten']),
  exportMode: z.enum(['single', 'multiple']).optional(),
}).refine(
  (data) => 
    // Personal info is always included, so check if at least one additional category is selected
    data.includeFinancialInfo || 
    data.includeCrmData || 
    data.includePolicyInfo || 
    data.includeComparisons ||
    data.includeFederalTSP ||
    data.includeFegli,
  {
    message: "Please select at least one additional data category to export (Personal Information is always included)",
    path: ["includeFinancialInfo"],
  }
);

type ExportOptions = z.infer<typeof exportClientSchema>;

export function ExportClientModal({ 
  client, 
  clients, 
  trigger, 
  onSuccess 
}: ExportClientModalProps) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const isBulkExport = !!clients && clients.length > 0;
  const exportCount = isBulkExport ? clients.length : 1;

  const methods = useForm<ExportOptions>({
    resolver: zodResolver(exportClientSchema),
    mode: "onChange",
    defaultValues: {
      format: 'csv',
      includePersonalInfo: true,
      includeFinancialInfo: true,
      includeCrmData: true,
      includePolicyInfo: false,
      includeComparisons: false,
      includeFederalTSP: true,
      includeFegli: false,
      jsonHandling: 'flatten',
      exportMode: isBulkExport ? 'single' : undefined,
    },
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isValid },
  } = methods;

  const selectedOptions = watch();

  // Ensure personal information is always included
  React.useEffect(() => {
    setValue('includePersonalInfo', true);
  }, [setValue]);
  
  // Count selected field groups
  const selectedCount = [
    selectedOptions.includePersonalInfo,
    selectedOptions.includeFinancialInfo,
    selectedOptions.includeCrmData,
    selectedOptions.includePolicyInfo,
    selectedOptions.includeComparisons,
    selectedOptions.includeFederalTSP,
    selectedOptions.includeFegli,
  ].filter(Boolean).length;

  // Estimate field count and file size
  const estimateFieldCount = () => {
    let count = 5; // Base fields (id, user_id, type, status, createdAt)
    if (selectedOptions.includePersonalInfo) count += 20;
    if (selectedOptions.includeFinancialInfo) count += 50;
    if (selectedOptions.includeCrmData) count += 7;
    if (selectedOptions.includePolicyInfo) count += 6;
    if (selectedOptions.includeComparisons) count += 5;
    if (selectedOptions.includeFederalTSP) count += 18;
    if (selectedOptions.includeFegli) count += 10;
    return count;
  };

  const estimateFileSize = () => {
    const fieldCount = estimateFieldCount();
    const sizePerClient = fieldCount * 50; // ~50 bytes per field average
    const totalSize = sizePerClient * exportCount;
    
    if (totalSize < 1024) return `~${totalSize} B`;
    if (totalSize < 1024 * 1024) return `~${Math.round(totalSize / 1024)} KB`;
    return `~${Math.round(totalSize / (1024 * 1024))} MB`;
  };

  // Generate filename
  const generateFilename = () => {
    const date = new Date().toISOString().split('T')[0];
    
    if (isBulkExport) {
      return `Clients_Export_${exportCount}_${date}.csv`;
    }
    
    if (client) {
      const name = `${client.firstName}_${client.lastName}`.replace(/\s+/g, '_');
      return `Client_${name}_${date}.csv`;
    }
    
    return `Client_Export_${date}.csv`;
  };

  // Select/Deselect All
  const selectAll = () => {
    setValue('includePersonalInfo', true);
    setValue('includeFinancialInfo', true);
    setValue('includeCrmData', true);
    setValue('includePolicyInfo', true);
    setValue('includeComparisons', true);
    setValue('includeFederalTSP', true);
    setValue('includeFegli', true);
  };

  const deselectAll = () => {
    // Personal info is required, so it stays checked
    setValue('includePersonalInfo', true);
    setValue('includeFinancialInfo', false);
    setValue('includeCrmData', false);
    setValue('includePolicyInfo', false);
    setValue('includeComparisons', false);
    setValue('includeFederalTSP', false);
    setValue('includeFegli', false);
  };

  const onSubmit = async (data: ExportOptions) => {
    // Validation is now handled by Zod schema
    setIsExporting(true);

    try {
      const filename = generateFilename();

      // Perform the export using CSVExporter
      if (isBulkExport && clients) {
        // Bulk export
        CSVExporter.exportClients(clients, data, filename);
      } else if (client) {
        // Single export
        CSVExporter.exportClient(client, data, filename);
      } else {
        throw new Error("No client data available for export");
      }

      toast({
        title: "Export Complete!",
        description: `Successfully exported ${exportCount} client${exportCount > 1 ? 's' : ''} to ${filename}`,
      });

      setIsExporting(false);
      setIsOpen(false);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error exporting client:", error);
      setIsExporting(false);
      
      toast({
        title: "Export Failed",
        description: error instanceof Error ? error.message : "Failed to export client data. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="bg-accent export-client-button">
            <Download className="mr-2 h-4 w-4" />
            Export to CSV
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogHeader className="space-y-3">
              <div className="flex items-center gap-2">
                <Download className="h-5 w-5 text-accent" />
                <DialogTitle className="text-xl">
                  {isBulkExport ? 'Export Multiple Clients' : 'Export Client Data'}
                </DialogTitle>
              </div>
              <DialogDescription className="text-base">
                {isBulkExport 
                  ? `Export ${exportCount} selected client${exportCount > 1 ? 's' : ''} to CSV format for analysis or backup`
                  : 'Export client data to CSV format for analysis, reporting, or backup purposes'
                }
              </DialogDescription>
            </DialogHeader>

            <Separator className="my-4" />

            <div className="grid gap-5 py-2">
              {/* Client Info Card - Enhanced */}
              {!isBulkExport && client && (
                <div className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-white rounded-full p-3 shadow-sm">
                      <FileSpreadsheet className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-base">
                        {client.firstName} {client.lastName}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm text-gray-600">
                          {client.type?.toUpperCase()} Plan
                        </span>
                        {client.status && (
                          <>
                            <span className="text-gray-400">•</span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              client.status === 'Active' ? 'bg-green-100 text-green-700' :
                              client.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {client.status}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Bulk Export Info - Enhanced */}
              {isBulkExport && clients && (
                <div className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-lg p-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-4">
                      <div className="bg-white rounded-full p-3 shadow-sm">
                        <FileSpreadsheet className="h-6 w-6 text-accent" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-base">
                          {exportCount} Selected Client{exportCount > 1 ? 's' : ''}
                        </p>
                        <p className="text-sm text-gray-600">
                          Ready for bulk export
                        </p>
                      </div>
                    </div>
                    {clients.length <= 5 ? (
                      <div className="mt-3 space-y-1 pl-14">
                        {clients.map(c => (
                          <p key={c.id} className="text-xs text-gray-600">
                            • {c.firstName} {c.lastName} ({c.type})
                          </p>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-3 space-y-1 pl-14">
                        {clients.slice(0, 3).map(c => (
                          <p key={c.id} className="text-xs text-gray-600">
                            • {c.firstName} {c.lastName} ({c.type})
                          </p>
                        ))}
                        <p className="text-xs text-gray-600 font-medium">
                          ... and {clients.length - 3} more
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Export Format - Enhanced */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-gray-700">
                  Export Format
                </Label>
                <Card className="border-green-200 bg-[hsl(120_60%_97%)]">
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-center space-x-3">
                      <div className="bg-green-600 rounded-full p-1.5">
                        <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            CSV Format
                          </span>
                          <span className="text-xs font-medium bg-green-100 text-green-700 px-2 py-0.5 rounded">
                            Universal Standard
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          Compatible with Excel, Google Sheets, Numbers, and all spreadsheet applications
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Select Data to Export - Enhanced */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-semibold text-gray-700">
                      Select Additional Data to Export
                    </Label>
                    <p className="text-xs text-gray-600 mt-1">
                      Personal Information is always included
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      // variant="ghost"
                      // size="sm"
                      onClick={selectAll}
                      className="text-xs h-7 hover:bg-gray-100 hover:text-accent"
                    >
                      Select All
                    </Button>
                    <Button
                      type="button"
                      // variant="ghost"
                      // size="sm"
                      onClick={deselectAll}
                      className="text-xs h-7 hover:bg-gray-100"
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
                <Card className="border-gray-200">
                  <CardContent className="pt-5 pb-4">
                    <div className="space-y-3">
                      {/* Personal Information - Always included */}
                      <div className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
                        <div className="bg-accent rounded p-1 mt-0.5">
                          <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium leading-none text-gray-900">
                            Personal Information
                            <span className="ml-2 text-xs font-normal text-accent">(Always Included)</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Name, DOB, Contact, Address (20 fields)
                          </p>
                        </div>
                      </div>

                      <Controller
                        name="includeFinancialInfo"
                        control={control}
                        render={({ field }) => (
                          <div className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${
                            field.value 
                              ? 'border border-green-200 bg-[hsl(120_60%_97%)]' 
                              : 'border border-transparent hover:bg-gray-50'
                          }`}>
                            <Checkbox
                              id="exportFinancialInfo"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <label
                                htmlFor="exportFinancialInfo"
                                className="text-sm font-medium leading-none cursor-pointer text-gray-900"
                              >
                                Financial Information
                              </label>
                              <p className="text-xs text-gray-500 mt-1">
                                Retirement plans, TSP, Income (50+ fields)
                              </p>
                            </div>
                          </div>
                        )}
                      />

                      <Controller
                        name="includeCrmData"
                        control={control}
                        render={({ field }) => (
                          <div className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${
                            field.value 
                              ? 'border border-green-200 bg-[hsl(120_60%_97%)]' 
                              : 'border border-transparent hover:bg-gray-50'
                          }`}>
                            <Checkbox
                              id="exportCrmData"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <label
                                htmlFor="exportCrmData"
                                className="text-sm font-medium leading-none cursor-pointer text-gray-900"
                              >
                                CRM Data
                              </label>
                              <p className="text-xs text-gray-500 mt-1">
                                Lifecycle stage, Contact history (7 fields)
                              </p>
                            </div>
                          </div>
                        )}
                      />

                      <Controller
                        name="includePolicyInfo"
                        control={control}
                        render={({ field }) => (
                          <div className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${
                            field.value 
                              ? 'border border-green-200 bg-[hsl(120_60%_97%)]' 
                              : 'border border-transparent hover:bg-gray-50'
                          }`}>
                            <Checkbox
                              id="exportPolicyInfo"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <label
                                htmlFor="exportPolicyInfo"
                                className="text-sm font-medium leading-none cursor-pointer text-gray-900"
                              >
                                Policy Information
                              </label>
                              <p className="text-xs text-gray-500 mt-1">
                                Insurance, Commission details (6 fields)
                              </p>
                            </div>
                          </div>
                        )}
                      />

                      <Controller
                        name="includeComparisons"
                        control={control}
                        render={({ field }) => (
                          <div className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${
                            field.value 
                              ? 'border border-green-200 bg-[hsl(120_60%_97%)]' 
                              : 'border border-transparent hover:bg-gray-50'
                          }`}>
                            <Checkbox
                              id="exportComparisons"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <label
                                htmlFor="exportComparisons"
                                className="text-sm font-medium leading-none cursor-pointer text-gray-900"
                              >
                                Comparisons
                              </label>
                              <p className="text-xs text-gray-500 mt-1">
                                Financial scenarios (JSON data)
                              </p>
                            </div>
                          </div>
                        )}
                      />

                      <Controller
                        name="includeFederalTSP"
                        control={control}
                        render={({ field }) => (
                          <div className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${
                            field.value 
                              ? 'border border-green-200 bg-[hsl(120_60%_97%)]' 
                              : 'border border-transparent hover:bg-gray-50'
                          }`}>
                            <Checkbox
                              id="exportFederalTSP"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <label
                                htmlFor="exportFederalTSP"
                                className="text-sm font-medium leading-none cursor-pointer text-gray-900"
                              >
                                Federal TSP
                              </label>
                              <p className="text-xs text-gray-500 mt-1">
                                Thrift Savings Plan data (18 fields)
                              </p>
                            </div>
                          </div>
                        )}
                      />

                      <Controller
                        name="includeFegli"
                        control={control}
                        render={({ field }) => (
                          <div className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${
                            field.value 
                              ? 'border border-green-200 bg-[hsl(120_60%_97%)]' 
                              : 'border border-transparent hover:bg-gray-50'
                          }`}>
                            <Checkbox
                              id="exportFegli"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <label
                                htmlFor="exportFegli"
                                className="text-sm font-medium leading-none cursor-pointer text-gray-900"
                              >
                                FEGLI Insurance
                              </label>
                              <p className="text-xs text-gray-500 mt-1">
                                Federal insurance options (10 fields)
                              </p>
                            </div>
                          </div>
                        )}
                      />
                    </div>
                    {errors.includeFinancialInfo && (
                      <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                        <Info className="h-3 w-3" />
                        {errors.includeFinancialInfo.message}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Complex Data Handling - Enhanced */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-gray-700">
                  Complex Data Format
                </Label>
                <p className="text-xs text-gray-600">
                  Choose how to display detailed financial scenarios and comparison data
                </p>
                <Card className="border-gray-200">
                  <CardContent className="pt-5 pb-4">
                    <Controller
                      name="jsonHandling"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup
                          value={field.value}
                          onValueChange={field.onChange}
                          className="space-y-3"
                        >
                          <div className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${
                            field.value === 'stringify'
                              ? 'border border-green-200 bg-[hsl(120_60%_97%)]' 
                              : 'border border-transparent hover:bg-gray-50'
                          }`}>
                            <RadioGroupItem value="stringify" id="stringify" className="mt-1" />
                            <div className="flex-1">
                              <label
                                htmlFor="stringify"
                                className="text-sm font-medium leading-none cursor-pointer text-gray-900"
                              >
                                Keep as Single Text
                              </label>
                              <p className="text-xs text-gray-500 mt-1">
                                Show all detailed data in one cell (compact but harder to analyze)
                              </p>
                            </div>
                          </div>
                          <div className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${
                            field.value === 'flatten'
                              ? 'border border-green-200 bg-[hsl(120_60%_97%)]' 
                              : 'border border-transparent hover:bg-gray-50'
                          }`}>
                            <RadioGroupItem value="flatten" id="flatten" className="mt-1" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <label
                                  htmlFor="flatten"
                                  className="text-sm font-medium leading-none cursor-pointer text-gray-900"
                                >
                                  Separate Columns
                                </label>
                                <span className="text-xs font-medium bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                  Recommended
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Break down data into individual columns for easy sorting and filtering
                              </p>
                            </div>
                          </div>
                        </RadioGroup>
                      )}
                    />
                  </CardContent>
                </Card>
              </div>

              {/* File Preview - Enhanced */}
              <Card className="bg-gradient-to-br from-gray-50 to-white border-gray-200">
                <CardContent className="pt-5 pb-4">
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4 text-accent" />
                      File Preview
                    </Label>
                    <div className="bg-white rounded-lg border border-gray-300 p-3">
                      <p className="text-sm font-mono text-gray-900 break-all">
                        {generateFilename()}
                      </p>
                    </div>
                    <div className="space-y-1.5 pt-2">
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-accent"></div>
                        <span>Estimated size: <span className="font-medium">{estimateFileSize()}</span></span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400"></div>
                        <span>Total fields: <span className="font-medium">~{estimateFieldCount()}</span></span>
                      </div>
                      {isBulkExport && exportCount > 50 && (
                        <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 rounded p-2 mt-2">
                          <Info className="h-3.5 w-3.5 flex-shrink-0" />
                          <span>Large export may take a few seconds...</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Separator className="mt-6" />

            <DialogFooter className="mt-6 gap-2">
              <Button 
                type="button" 
                // variant="outline" 
                onClick={() => setIsOpen(false)}
                disabled={isExporting}
                className="min-w-[100px]"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-accent hover:opacity-90 min-w-[140px]"
                disabled={!isValid || isExporting || selectedCount === 0}
              >
                {isExporting ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Export {isBulkExport ? `${exportCount} Client${exportCount > 1 ? 's' : ''}` : 'CSV'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}

